/*
 * Envios.js — Piezas del diálogo de envíos a clientes (C-010, ver diseno/dialogos.md sección C).
 *
 * El riesgo real de un envío es equivocarse de mes o mandar dos veces, así que el diálogo
 * pone al frente: mes, destinatarios por canal, a quién no le llega y si ya se envió.
 */

export const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// Igual que el backend (separarEmailsValidos): direcciones separadas por coma o punto y coma.
export const correosValidos = (email) =>
  String(email || '')
    .split(/[;,]/)
    .map(e => e.trim())
    .filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

export const tieneTelefono = (telefono) => !!(telefono && String(telefono).trim() !== '');

const formatoFecha = (valor) => {
  if (!valor) return '';
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return '';
  return fecha.toLocaleString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function ListaDesplegable({ titulo, personas }) {
  if (!personas || personas.length === 0) return null;
  return (
    <details className="envio-lista">
      <summary>Ver cuáles</summary>
      <ul aria-label={titulo}>
        {personas.map(p => (
          <li key={p.id ?? p.nombre}>{p.nombre}{p.motivo ? ` · ${p.motivo}` : ''}</li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Cuerpo del diálogo de envío.
 *   mes             "Septiembre 2026" (se muestra en grande) — opcional
 *   etiquetaMes     "Mes que se solicita"
 *   destinatarios   texto: "74 clientes · 80 correos"
 *   soloTelefono    [{ id, nombre }] clientes que solo tienen teléfono (WhatsApp no está activo)
 *   sinContacto     [{ id, nombre, motivo }]
 *   yaEnviado       { enviados, ultimo } | null → aviso de envío doble
 *   nota            texto adicional
 */
export function ResumenEnvio({ mes, etiquetaMes, destinatarios, soloTelefono = [], sinContacto = [], yaEnviado, nota }) {
  return (
    <>
      {mes && (
        <div className="dialogo-mes">
          <span>{etiquetaMes}</span>
          <strong>{mes}</strong>
        </div>
      )}
      <dl className="envio-resumen">
        <dt>Destinatarios</dt>
        <dd>{destinatarios}</dd>
        {soloTelefono.length > 0 && (
          <>
            <dt>Solo tienen teléfono</dt>
            <dd>
              {plural(soloTelefono.length, 'cliente', 'clientes')}. El envío por WhatsApp todavía no está activo, así que <strong>no recibirán nada</strong>.
              <ListaDesplegable titulo="Clientes que solo tienen teléfono" personas={soloTelefono} />
            </dd>
          </>
        )}
        {sinContacto.length > 0 && (
          <>
            <dt>No se enviará a</dt>
            <dd>
              {plural(sinContacto.length, 'cliente', 'clientes')} sin correo válido ni teléfono.
              <ListaDesplegable titulo="Clientes sin contacto" personas={sinContacto} />
            </dd>
          </>
        )}
      </dl>
      {yaEnviado && (
        <p className="envio-aviso" role="alert">
          <strong>Ya se envió este mes</strong> ({plural(yaEnviado.enviados, 'correo', 'correos')}
          {yaEnviado.ultimo ? `, el último el ${formatoFecha(yaEnviado.ultimo)}` : ''}).
          Si envías otra vez, los clientes la recibirán dos veces.
        </p>
      )}
      {nota && <p>{nota}</p>}
    </>
  );
}

/** Resultado con fallos: "71 enviados, 3 fallaron · Ver detalle". */
export function ResultadoEnvio({ enviados, unidad = ['envío', 'envíos'], fallidos = [], extra }) {
  return (
    <>
      <p className="envio-resultado">
        <strong>{plural(enviados, `${unidad[0]} enviado`, `${unidad[1]} enviados`)}</strong>
        {fallidos.length > 0 && <>, <strong className="envio-fallidos">{plural(fallidos.length, 'falló', 'fallaron')}</strong></>}.
      </p>
      {fallidos.length > 0 && (
        <details className="envio-lista" open={fallidos.length <= 5}>
          <summary>Ver detalle</summary>
          <ul>
            {fallidos.map((f, i) => <li key={i}>{f.nombre}{f.razon ? ` · ${f.razon}` : ''}</li>)}
          </ul>
        </details>
      )}
      {extra && <p>{extra}</p>}
    </>
  );
}

/** Texto de progreso del envío masivo según la fase que reporta el servidor. */
export const textoProgreso = (p) => {
  if (!p || !p.fase) return 'Enviando…';
  if (p.fase === 'correos') return `Enviando correos… ${p.hechos} de ${p.total}`;
  if (p.fase === 'revisando') return `Revisando clientes… ${p.hechos} de ${p.total}`;
  return `Terminando el envío… ${p.hechos} de ${p.total}`;
};

const esperar = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Espera a que termine el envío masivo que corre en el servidor, reportando el progreso.
 * Devuelve el estado final ({ enviados, fallidos, ... }).
 */
export async function esperarEnvioMasivo(consultar, reportar, intervaloMs = 1500) {
  for (;;) {
    const p = await consultar();
    if (!p || !p.activo) return p || {};
    reportar(textoProgreso(p));
    await esperar(intervaloMs);
  }
}
